import React, {useState, useEffect} from "react";
import {useAuth} from "../../hooks/useAuth";
import {useNavigate} from "react-router-dom";
import SessionCard from "../../components/SessionCard";

import "../../styles/env.css";

// Import Firebase hooks
import {
  useSessionsRealtime,
  useCharactersByPlayerRealtime,
  useSessionsByDM,
  useUpcomingSessions,
  useActiveDMs,
  useActivePlayers,
  useTitansRealtime,
} from "../../hooks/useFirestore";

import {useNotifications} from "../../components/NotificationSystem";
import {
  checkUserDMApproval,
  withdrawDMApplication,
} from "../../services/api/playersService";
import {getDisplayDate, isSessionDone} from "../../utils/dateUtils";
import {SessionService} from "../../services/api/sessionService";

const Overview: React.FC = () => {
  const {roles, user} = useAuth();
  const navigate = useNavigate();
  const {addNotification} = useNotifications();

  // Firebase data hooks
  const {data: allSessions, loading: sessionsLoading} = useSessionsRealtime();
  const {data: userCharacters, loading: charactersLoading} =
    useCharactersByPlayerRealtime(user?.uid || null);
  const {data: userSessions, loading: userSessionsLoading} = useSessionsByDM(
    user?.uid || ""
  );
  const {data: upcomingSessions} = useUpcomingSessions();
  const {data: activeDMs, loading: dmsLoading} = useActiveDMs();
  const {data: activePlayers, loading: playersLoading} = useActivePlayers();
  const {data: titans, loading: titansLoading} = useTitansRealtime();

  // Function to resolve titan ID to titan name
  const getTitanName = (titanId: string): string => {
    if (!titans || titans.length === 0) {
      return titanId; // Return ID if titans aren't loaded
    }

    const titan = titans.find((t) => t.id === titanId);
    return titan ? titan.name : titanId; // Fallback to ID if not found
  };

  // Collapsible state for each section
  const [open, setOpen] = useState({
    player: true,
    dm: true,
    moderator: true,
    admin: true,
  });
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(
    null
  );
  const [viewLogSession, setViewLogSession] = useState<any | null>(null);
  const [hasPendingDMApproval, setHasPendingDMApproval] = useState(false);
  const [isWithdrawingDM, setIsWithdrawingDM] = useState(false);

  const toggle = (key: keyof typeof open) => {
    setOpen((prev) => ({...prev, [key]: !prev[key]}));
  };

  // Check for pending DM approval
  useEffect(() => {
    const checkPendingDMApproval = async () => {
      if (!user || !roles.includes("player")) {
        return;
      }

      try {
        // Check if user has a pending DM approval
        const data = await checkUserDMApproval(user.uid);
        setHasPendingDMApproval(data.hasPendingApproval);
      } catch (error) {
        // Error checking DM approval status - setting to false as fallback
        setHasPendingDMApproval(false);
      }
    };

    checkPendingDMApproval();
  }, [user, roles]);

  // Calculate statistics from real data
  const stats = {
    totalPlayers: activePlayers?.length || 0,
    activeDMs: activeDMs?.length || 0,
    activePlayers: activePlayers?.length || 0,
    totalSessions: allSessions?.length || 0,
  };

  // Helper: is user signed up for a session?
  const isSignedUp = (session: any) => {
    if (!user?.uid) return false;
    return (
      session.players &&
      session.players.some((p: any) => p.playerId === user.uid)
    );
  };

  // Filter sessions for current user
  const completedDMSessions =
    userSessions?.filter((s: any) => {
      return s.status === "completed" || isSessionDone(s.date);
    }) || [];
  const upcomingDMSessions =
    userSessions?.filter((s: any) => {
      return !(s.status === "completed" || isSessionDone(s.date));
    }) || [];

  // Filter upcoming sessions where user is signed up
  const userUpcomingSessions =
    upcomingSessions?.filter((session) => {
      return !isSessionDone(session.date) && isSignedUp(session);
    }) || [];

  // Track session log status for player-attended sessions
  const [sessionLogStatus, setSessionLogStatus] = useState<
    Record<string, boolean>
  >({});

  // Check session log status for completed or past sessions where the user participated
  useEffect(() => {
    const checkSessionLogStatus = async () => {
      if (!user?.uid || !allSessions) return;

      const statusChecks: Record<string, boolean> = {};

      for (const session of allSessions) {
        const isCompleted =
          session.status === "completed" || isSessionDone(session.date);

        if (isCompleted) {
          const playerInSession = session.players?.find(
            (p: any) => p.playerId === user.uid
          );
          if (playerInSession) {
            try {
              const result = await SessionService.checkSessionLogStatus({
                sessionId: session.id!,
                playerId: user.uid,
                characterId: playerInSession.characterId,
              });

              const sessionKey = `${session.id}_${playerInSession.characterId}`;
              statusChecks[sessionKey] = result.hasLogged;
            } catch (error) {
              const sessionKey = `${session.id}_${playerInSession.characterId}`;
              statusChecks[sessionKey] = false;
            }
          }
        }
      }

      setSessionLogStatus(statusChecks);
    };

    checkSessionLogStatus();
  }, [user?.uid, allSessions]);

  // Sessions to log: attended by user, completed or in the past, and not yet logged
  const userSessionsToLog = (allSessions || []).filter((session: any) => {
    const isCompleted = session.status === "completed" || isSessionDone(session.date);
    if (!isCompleted) return false;
    if (!isSignedUp(session)) return false;
    const playerInSession = session.players?.find(
      (p: any) => p.playerId === user?.uid
    );
    if (!playerInSession) return false;
    const sessionKey = `${session.id}_${playerInSession.characterId}`;
    return sessionLogStatus[sessionKey] === false;
  });

  // Handler: read log (navigate to session management)
  const handleReadLog = () => {
    navigate("/sessionmanagement");
  };
  const handleCloseLog = () => {
    setViewLogSession(null);
  };

  // Handle withdraw DM application
  const handleWithdrawDMApplication = async () => {
    if (!user?.uid) return;

    setIsWithdrawingDM(true);
    try {
      await withdrawDMApplication();

      // Update local state
      setHasPendingDMApproval(false);

      // Show success notification
      addNotification({
        type: "success",
        title: "DM Application Withdrawn",
        message: "Your DM application has been withdrawn successfully.",
      });
    } catch (error: any) {
      // Error withdrawing DM application - handled by notification system
      addNotification({
        type: "error",
        title: "Withdrawal Failed",
        message:
          error.message ||
          "Failed to withdraw DM application. Please try again.",
      });
    } finally {
      setIsWithdrawingDM(false);
    }
  };

  // Render log view if set
  if (viewLogSession) {
    const log = viewLogSession.sessionLog;
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Log</h2>
        <section className="env-section">
          <div className="env-log-header">
            <span>[{viewLogSession.name}] NOTES AND LOGS</span>
            <button className="btn" onClick={handleCloseLog}>
              Back
            </button>
          </div>
          <div className="env-log-dm-notes">
            <div className="env-log-label">DM Notes</div>
            <div className="env-log-notes-content">
              {log?.events || "No notes available."}
            </div>
          </div>
          <div className="env-log-items-section">
            <div className="env-log-label">Items received</div>
            <table className="env-log-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Quantity</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {log?.items && log.items.length > 0 ? (
                  log.items.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td>{item.name}</td>
                      <td>{item.amount}</td>
                      <td>{item.description}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>No items recorded.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }

  // Show loading state if any critical data is loading
  const isLoading =
    sessionsLoading ||
    charactersLoading ||
    userSessionsLoading ||
    dmsLoading ||
    playersLoading ||
    titansLoading;

  if (isLoading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Dashboard</h2>
        <div style={{textAlign: "center", padding: "2rem"}}>
          Loading dashboard data...
        </div>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Dashboard</h2>

      {/* PLAYER SECTION */}
      {(roles.includes("player") ||
        roles.includes("dm") ||
        roles.includes("moderator") ||
        roles.includes("admin")) && (
        <section className="env-overview-section">
          <button
            className="env-overview-header"
            onClick={() => toggle("player")}
          >
            Player
            <span className="env-overview-toggle">
              {open.player ? "▾" : "▸"}
            </span>
          </button>
          {open.player && (
            <div className="env-overview-content">
              {/* Stats */}
              <div className="env-overview-stats">
                <div className="env-overview-stat">
                  <div className="env-overview-stat-label">
                    Sessions attended
                  </div>
                  <div className="env-overview-stat-value">
                    {completedDMSessions.length}
                  </div>
                </div>
                <div className="env-overview-stat">
                  <div className="env-overview-stat-label">Memento tokens</div>
                  <div className="env-overview-stat-value">
                    {userCharacters?.reduce(
                      (total, char) => total + (char.mementoTokens || 0),
                      0
                    ) || 0}
                  </div>
                </div>
                {(roles.includes("dm") ||
                  roles.includes("moderator") ||
                  roles.includes("admin")) && (
                  <div className="env-overview-stat">
                    <div className="env-overview-stat-label">DM tokens</div>
                    <div className="env-overview-stat-value">
                      {user?.dmTokens ?? 0}
                    </div>
                  </div>
                )}
              </div>

              {/* DM Status Pending Notice */}
              {hasPendingDMApproval && (
                <div className="env-dm-pending-notice">
                  <div className="env-dm-pending-icon">⏳</div>
                  <div className="env-dm-pending-content">
                    <div className="env-dm-pending-title">
                      DM Status Pending
                    </div>
                    <div className="env-dm-pending-message">
                      Your DM application is currently under review. You'll
                      receive an email notification once your account has been
                      approved.
                    </div>
                    <button
                      className="btn btn-secondary"
                      onClick={handleWithdrawDMApplication}
                      disabled={isWithdrawingDM}
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        padding: "4px 8px",
                      }}
                    >
                      {isWithdrawingDM
                        ? "Withdrawing..."
                        : "Withdraw Application"}
                    </button>
                  </div>
                </div>
              )}
              {/* Characters */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">Your characters</div>
                <div className="env-overview-card-stats">
                  <div className="env-overview-card-stat">
                    <div className="env-overview-card-stat-label">
                      Active characters
                    </div>
                    <div className="env-overview-card-stat-value">
                      {userCharacters?.filter((char) => char.isActive).length ||
                        0}
                    </div>
                  </div>
                </div>

                {/* Character List */}
                <div className="env-character-list">
                  {userCharacters && userCharacters.length > 0 ? (
                    userCharacters
                      .filter((char) => char.isActive)
                      .map((character) => (
                        <div key={character.id} className="env-character-item">
                          <div className="env-character-info">
                            <h3>{character.name}</h3>
                            <div className="env-character-details">
                              Level {character.level} {character.class}
                            </div>
                            <div className="env-character-tokens">
                              {character.lastPlayed
                                ? `Last played: ${getDisplayDate(character.lastPlayed)}`
                                : "Never played"}
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="env-empty-state env-empty-in-card">
                      No characters available
                    </div>
                  )}
                </div>

                <div className="env-overview-card-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate("/characters")}
                  >
                    Go to Characters
                  </button>
                </div>
              </div>
              {/* Upcoming Sessions */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">
                  My upcoming sessions
                </div>
                <div className="env-item-list">
                  {userUpcomingSessions?.slice(0, 3).map((session: any) => (
                    <SessionCard
                      key={session.id}
                      id={session.id}
                      title={session.name}
                      date={getDisplayDate(session.date)}
                      duration={session.duration}
                      location={session.location}
                      titan={getTitanName(session.titanId)}
                      levelRange={session.levelRange || "Level 1-20"}
                      maxPlayers={session.maxPlayers}
                      players={session.players || []}
                      isOpen={expandedSessionId === session.id}
                      onToggleOpen={() =>
                        setExpandedSessionId(
                          expandedSessionId === session.id ? null : session.id
                        )
                      }
                    />
                  ))}
                  {(!userUpcomingSessions ||
                    userUpcomingSessions.length === 0) && (
                    <div className="env-empty-state env-empty-in-card">
                      No upcoming sessions
                    </div>
                  )}
                </div>
                <div className="env-overview-card-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate("/sessions?status=upcoming")}
                  >
                    View All Upcoming
                  </button>
                </div>
              </div>

              {/* Completed Sessions to Log */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">Sessions to log</div>
                <div className="env-item-list">
                  {userSessionsToLog?.slice(0, 3).map((session: any) => (
                    <SessionCard
                      key={session.id}
                      id={session.id}
                      title={session.name}
                      date={getDisplayDate(session.date)}
                      duration={session.duration}
                      location={session.location}
                      titan={getTitanName(session.titanId)}
                      levelRange={session.levelRange || "Level 1-20"}
                      maxPlayers={session.maxPlayers}
                      players={session.players || []}
                      isOpen={expandedSessionId === session.id}
                      onToggleOpen={() =>
                        setExpandedSessionId(
                          expandedSessionId === session.id ? null : session.id
                        )
                      }
                      secondaryActionLabel="Submit log"
                      onSecondaryAction={() =>
                        navigate(
                          `/sessions?sessionId=${session.id}&openLog=true`
                        )
                      }
                    />
                  ))}
                  {(!userSessionsToLog || userSessionsToLog.length === 0) && (
                    <div className="env-empty-state env-empty-in-card">
                      No sessions need logging
                    </div>
                  )}
                </div>
                <div className="env-overview-card-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate("/sessions?status=completed")}
                  >
                    View All Completed
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* DM SECTION */}
      {(roles.includes("dm") ||
        roles.includes("moderator") ||
        roles.includes("admin")) && (
        <section className="env-overview-section">
          <button className="env-overview-header" onClick={() => toggle("dm")}>
            DM
            <span className="env-overview-toggle">{open.dm ? "▾" : "▸"}</span>
          </button>
          {open.dm && (
            <div className="env-overview-content">
              {/* Stats */}
              <div className="env-overview-stats">
                <div className="env-overview-stat">
                  <div className="env-overview-stat-label">
                    Total sessions created
                  </div>
                  <div className="env-overview-stat-value">
                    {userSessions?.length || 0}
                  </div>
                </div>
                <div className="env-overview-stat">
                  <div className="env-overview-stat-label">
                    Total players signed up for sessions
                  </div>
                  <div className="env-overview-stat-value">
                    {userSessions?.reduce(
                      (total, session) =>
                        total + (session.players?.length || 0),
                      0
                    ) || 0}
                  </div>
                </div>
              </div>
              {/* Upcoming Sessions */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">Upcoming sessions</div>
                <div className="env-item-list">
                  {upcomingDMSessions?.map((session: any) => (
                    <SessionCard
                      key={session.id}
                      id={session.id}
                      title={session.name}
                      date={getDisplayDate(session.date)}
                      duration={session.duration}
                      location={session.location}
                      titan={getTitanName(session.titanId)}
                      levelRange={session.levelRange || "Level 1-20"}
                      maxPlayers={session.maxPlayers}
                      players={session.players || []}
                      isOpen={expandedSessionId === session.id}
                      onToggleOpen={() =>
                        setExpandedSessionId(
                          expandedSessionId === session.id ? null : session.id
                        )
                      }
                      secondaryActionLabel={
                        session.sessionLog ? "Read log" : undefined
                      }
                      onSecondaryAction={
                        session.sessionLog ? () => handleReadLog() : undefined
                      }
                    />
                  ))}
                </div>
                <div className="env-overview-card-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate("/sessionmanagement")}
                  >
                    Go to Session Management
                  </button>
                </div>
              </div>
              {/* Completed Sessions */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">
                  Completed Sessions
                </div>
                <div className="env-item-list">
                  {completedDMSessions?.length === 0 ? (
                    <div className="env-empty-state env-empty-in-card">
                      No completed sessions available
                    </div>
                  ) : (
                    completedDMSessions?.map((session: any) => (
                      <SessionCard
                        key={session.id}
                        id={session.id}
                        title={session.name}
                        date={getDisplayDate(session.date)}
                        duration={session.duration}
                        location={session.location}
                        titan={getTitanName(session.titanId)}
                        levelRange={session.levelRange || "Level 1-20"}
                        maxPlayers={session.maxPlayers}
                        players={session.players || []}
                        secondaryActionLabel={"See notes and logs"}
                        onSecondaryAction={() => {
                          localStorage.setItem("openSessionId", session.id);
                          navigate("/sessionmanagement");
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* MODERATOR SECTION */}
      {roles.includes("moderator") && (
        <section className="env-overview-section">
          <button
            className="env-overview-header"
            onClick={() => toggle("moderator")}
          >
            Moderator
            <span className="env-overview-toggle">
              {open.moderator ? "▾" : "▸"}
            </span>
          </button>
          {open.moderator && (
            <div className="env-overview-content">
              {/* Quick Actions */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">Quick actions</div>
                <div className="env-overview-actions">
                  <button
                    className="btn btn-secondary env-btn-full env-overview-action-btn"
                    onClick={() => navigate("/dm-approvals")}
                  >
                    Pending approvals
                  </button>
                  <button
                    className="btn btn-secondary env-btn-full env-overview-action-btn"
                    onClick={() => navigate("/feedback-management")}
                  >
                    Feedback management
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ADMIN SECTION */}
      {roles.includes("admin") && (
        <section className="env-overview-section">
          <button
            className="env-overview-header"
            onClick={() => toggle("admin")}
          >
            Admin
            <span className="env-overview-toggle">
              {open.admin ? "▾" : "▸"}
            </span>
          </button>
          {open.admin && (
            <div className="env-overview-content">
              {/* Quick Actions */}
              <div className="env-overview-card">
                <div className="env-overview-card-title">Quick actions</div>
                <div className="env-overview-actions">
                  <button
                    className="btn btn-secondary env-btn-full env-overview-action-btn"
                    onClick={() => navigate("/dm-approvals")}
                  >
                    Pending approvals
                  </button>
                  <button className="btn btn-secondary env-btn-full env-overview-action-btn">
                    Send announcement
                  </button>
                  <button className="btn btn-secondary env-btn-full env-overview-action-btn">
                    View system logs
                  </button>
                </div>
              </div>
              {/* Stats */}
              <div className="env-overview-stats">
                <div className="env-overview-stat-small">
                  <div className="env-overview-stat-label-small">
                    Total users
                  </div>
                  <div className="env-overview-stat-value-small">
                    {stats.totalPlayers}
                  </div>
                </div>
                <div className="env-overview-stat-small">
                  <div className="env-overview-stat-label-small">
                    Active DMs
                  </div>
                  <div className="env-overview-stat-value-small">
                    {stats.activeDMs}
                  </div>
                </div>
                <div className="env-overview-stat-small">
                  <div className="env-overview-stat-label-small">
                    Active Players
                  </div>
                  <div className="env-overview-stat-value-small">
                    {stats.activePlayers}
                  </div>
                </div>
                <div className="env-overview-stat-small">
                  <div className="env-overview-stat-label-small">
                    Total sessions
                  </div>
                  <div className="env-overview-stat-value-small">
                    {stats.totalSessions}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Overview;
